/**
 * A condition step's rules — journey checks and catalogue rules under one
 * ALL/ANY join.
 *
 * ## THE SEGMENT BUILDER'S PARTS, REUSED
 *
 * A catalogue rule in a condition is chosen with slice 67's `FieldPicker` (the
 * same search, groups, coverage bars and keyboard model as the segment
 * builder) and valued with its `RuleValueEditor` (the same editors writing the
 * same contract shapes), from `components/rules`. So a rule written here and
 * the same rule in a segment are the same JSON, read the same way, edited with
 * the same controls. What is the condition's own is the journey checks —
 * "replied since entering" — which are not catalogue fields and so are a plain
 * select, and the row's choice between the two.
 *
 * (Superseded: while slices 67 and 69 were built side by side this file drew
 * its own field select and value inputs, because `components/rules/` did not
 * exist yet. It was reconciled once it did.)
 *
 * A condition is flat (one join, up to 20 rows) because `flowConditionData`
 * is flat — nested groups belong in a rule segment, which a condition can
 * already ask about through the catalogue.
 */
import {
  FLOW_JOURNEY_FIELD_LABEL,
  FLOW_JOURNEY_FIELD_VALUES,
  OPERATORS_BY_TYPE,
  operatorLabel,
  type Field,
  type FieldType,
  type FlowConditionData,
  type FlowJourneyField,
  type RuleLeaf,
  type RuleOperator,
} from '@wend/shared';
import { SegmentedToggle } from '@wend/ui';
import { useMemo } from 'react';

import { FieldPicker, RuleValueEditor } from '../rules';
import { CTRL, HINT } from './controls';
import { IcPlus, IcTrash } from './icons';
import { conditionFields, defaultLeafValue, fieldInfo, isJourneyRule, leafFor } from './model';

type Rule = FlowConditionData['rules'][number];

const JOURNEY_PREFIX = 'journey:';

export interface ConditionRulesProps {
  data: FlowConditionData;
  fields: readonly Field[] | null;
  onChange: (data: FlowConditionData) => void;
  newId: () => string;
}

export function ConditionRules({ data, fields, onChange, newId }: ConditionRulesProps) {
  const pickable = useMemo(() => conditionFields(fields), [fields]);

  const setRule = (id: string, rule: Rule) => onChange({ ...data, rules: data.rules.map((r) => (r.id === id ? rule : r)) });
  const remove = (id: string) => onChange({ ...data, rules: data.rules.filter((r) => r.id !== id) });

  const pickField = (rule: Rule, value: string) => {
    if (value.startsWith(JOURNEY_PREFIX)) {
      setRule(rule.id, { kind: 'journey', id: rule.id, field: value.slice(JOURNEY_PREFIX.length) as FlowJourneyField, op: 'is_true' });
      return;
    }
    const field = pickable.find((f) => f.key === value);
    if (field) setRule(rule.id, leafFor(field, rule.id));
  };

  return (
    <div className="flex flex-col gap-2.5">
      <p className="m-0 font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted">Rules — the same fields and operators as segments</p>
      {data.rules.length > 1 && (
        <SegmentedToggle
          label="Match"
          value={data.join}
          onValueChange={(join) => onChange({ ...data, join: join === 'or' ? 'or' : 'and' })}
          options={[
            { value: 'and', label: 'All rules', sub: 'AND' },
            { value: 'or', label: 'Any rule', sub: 'OR' },
          ]}
        />
      )}
      {data.rules.map((rule, i) => {
        const field = isJourneyRule(rule) ? undefined : pickable.find((f) => f.key === rule.field);
        const info = isJourneyRule(rule) ? null : fieldInfo(fields, rule.field);
        const type: FieldType = info?.type ?? 'text';
        const selectValue = isJourneyRule(rule) ? `${JOURNEY_PREFIX}${rule.field}` : rule.field;
        return (
          <div key={rule.id} className="flex flex-col gap-2 rounded-[10px] border border-line bg-white/[0.02] p-2.5">
            <div className="flex min-h-6 items-center justify-between font-mono text-[10.5px] uppercase tracking-[0.1em] text-brand-b">
              <span>{i === 0 ? 'Where' : data.join.toUpperCase()}</span>
              {data.rules.length > 1 && (
                <button type="button" aria-label="Remove rule" onClick={() => remove(rule.id)} className="grid size-[26px] cursor-pointer place-items-center rounded-lg border border-line bg-white/5 text-muted hover:text-white focus-visible:outline-2 focus-visible:outline-brand-b">
                  <IcTrash size={14} />
                </button>
              )}
            </div>
            {pickable.length > 0 && (
              <SegmentedToggle
                size="sm"
                label="Rule kind"
                value={isJourneyRule(rule) ? 'journey' : 'field'}
                onValueChange={(kind) => {
                  if (kind === 'journey' && !isJourneyRule(rule)) pickField(rule, `${JOURNEY_PREFIX}replied_since_entry`);
                  if (kind === 'field' && isJourneyRule(rule) && pickable[0]) pickField(rule, pickable[0].key);
                }}
                options={[
                  { value: 'journey', label: 'This journey' },
                  { value: 'field', label: 'A field' },
                ]}
              />
            )}
            {isJourneyRule(rule) ? (
              <div className="flex flex-wrap gap-1.5">
                <select aria-label="Journey event" className={`${CTRL} flex-[1_1_180px]`} value={selectValue} onChange={(e) => pickField(rule, e.target.value)}>
                  {FLOW_JOURNEY_FIELD_VALUES.map((key) => (
                    <option key={key} value={`${JOURNEY_PREFIX}${key}`}>{FLOW_JOURNEY_FIELD_LABEL[key]}</option>
                  ))}
                </select>
                <select aria-label="Operator" className={`${CTRL} flex-[1_1_90px]`} value={rule.op} onChange={(e) => setRule(rule.id, { ...rule, op: e.target.value === 'is_false' ? 'is_false' : 'is_true' })}>
                  <option value="is_true">yes</option>
                  <option value="is_false">no</option>
                </select>
              </div>
            ) : (
              <>
                <FieldPicker fields={pickable} value={rule.field} onChange={(key) => pickField(rule, key)} />
                {!field && <p className={`${HINT} text-warn`}>“{info?.label ?? rule.field}” is not in the catalogue any more — choose another field.</p>}
                <div className="flex flex-wrap items-center gap-1.5">
                  <select
                    aria-label="Operator"
                    className={`${CTRL} flex-[1_1_150px]`}
                    value={rule.op}
                    onChange={(e) => {
                      const op = e.target.value as RuleOperator;
                      const value = defaultLeafValue(op, type, field);
                      setRule(rule.id, { kind: 'rule', id: rule.id, field: rule.field, op, ...(value === undefined ? {} : { value }) } as RuleLeaf);
                    }}
                  >
                    {OPERATORS_BY_TYPE[type].map((op) => (
                      <option key={op} value={op}>{operatorLabel(op, type).label}</option>
                    ))}
                  </select>
                  <RuleValueEditor
                    op={rule.op}
                    type={type}
                    field={field}
                    value={'value' in rule ? rule.value : undefined}
                    onChange={(value) => setRule(rule.id, { ...rule, value } as RuleLeaf)}
                  />
                </div>
              </>
            )}
            {field?.needsDateFormat && <p className={`${HINT} text-warn`}>This column’s dates are ambiguous. Choose day-first or month-first under Audiences → Fields before using it.</p>}
          </div>
        );
      })}
      {data.rules.length < 20 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onChange({ ...data, rules: [...data.rules, { kind: 'journey', id: newId(), field: 'converted_since_entry', op: 'is_true' }] })}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-[9px] border border-line-strong bg-white/5 px-3 py-2 text-[13px] font-medium text-text hover:bg-white/[0.09] focus-visible:outline-2 focus-visible:outline-brand-b"
          >
            <IcPlus size={14} /> Journey check
          </button>
          {pickable.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const first = pickable[0];
                if (first) onChange({ ...data, rules: [...data.rules, leafFor(first, newId())] });
              }}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-[9px] border border-line-strong bg-white/5 px-3 py-2 text-[13px] font-medium text-text hover:bg-white/[0.09] focus-visible:outline-2 focus-visible:outline-brand-b"
            >
              <IcPlus size={14} /> Field rule
            </button>
          )}
        </div>
      )}
      {fields === null && <p className={HINT}>The field catalogue could not be read, so only journey checks are offered right now.</p>}
    </div>
  );
}
